import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
  NamedRedirect,
  Avatar,
} from '../../components';
import { TopbarContainer } from '../../containers';

import { loadData } from './PreSignupPage.duck';

import css from './PreSignupPage.css';

// Pre-signup step shown when a logged-out visitor clicks a freelancer CTA on
// a skill SEO page: nudges them to create an account first, showing the
// clicked freelancer's avatar and first name as social proof.
export const PreSignupPageComponent = props => {
  const { intl, scrollingDisabled, currentUser, user } = props;

  // Logged-in users don't need the signup nudge.
  if (currentUser) {
    return <NamedRedirect name="DashboardPage" />;
  }

  const schemaTitle = intl.formatMessage({ id: 'PreSignupPage.schemaTitle' });

  const displayName = user?.attributes?.profile?.displayName || '';
  const firstName = displayName.split(' ')[0];

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
      noIndexing
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="PreSignupPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.content}>
            <h1 className={css.title}>
              <FormattedMessage id="PreSignupPage.title" />
            </h1>
            <div className={css.subtitleRow}>
              {user ? (
                <Avatar
                  className={css.avatar}
                  user={user}
                  disableProfileLink={true}
                  disableVerifiedBadge={true}
                />
              ) : null}
              <p className={css.subtitle}>
                {firstName ? (
                  <FormattedMessage id="PreSignupPage.subtitle" values={{ name: firstName }} />
                ) : (
                  <FormattedMessage id="PreSignupPage.subtitleGeneric" />
                )}
              </p>
            </div>
            <NamedLink name="SignupPage" className={css.ctaButton}>
              <FormattedMessage id="PreSignupPage.cta" />
            </NamedLink>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

PreSignupPageComponent.defaultProps = {
  currentUser: null,
  user: null,
};

PreSignupPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: object,
  user: object,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { user } = state.PreSignupPage;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const PreSignupPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(PreSignupPageComponent);

PreSignupPage.loadData = loadData;

export default PreSignupPage;
